"use client";

import { useEffect } from "react";

// The body background is painted from the page content's .X-body class via
// body:has(.X-body) rules in styles/index.css. Those rules are correct for
// server-rendered loads, but on soft navigation the Next.js router (16.3+)
// keeps the previous page's DOM in the document, hidden by React <Activity>
// with an inline display:none. :has() matches hidden elements too - and
// Chromium doesn't re-evaluate :has() when only an inline style changes -
// so the stale page's color class competes with the visible one.
//
// This component mirrors the VISIBLE page's color onto body[data-bg], which
// the body[data-bg="..."] rules in styles/index.css pick up (they win over
// the :has() rules by source order).
//
// Ordered as the body:has(.X-body) rules appear in styles/index.css: when
// several colored sections are visible at once, the LAST rule in the
// stylesheet used to win the cascade, so the highest index wins here too.
const CSS_RULE_ORDER = ["yellow", "blue", "red", "green", "brown"] as const;

export default function BodyBackgroundSync() {
  useEffect(() => {
    const main = document.querySelector("main");
    if (!main) return;

    const sync = () => {
      let winner = -1;
      for (const el of main.querySelectorAll<HTMLElement>('[class*="-body"]')) {
        for (const cls of el.classList) {
          if (!cls.endsWith("-body")) continue;
          const index = CSS_RULE_ORDER.indexOf(
            cls.slice(0, -"-body".length) as (typeof CSS_RULE_ORDER)[number],
          );
          if (index <= winner) continue;
          // checkVisibility() is what excludes Activity-hidden stale pages
          if (el.checkVisibility?.() === false) continue;
          winner = index;
        }
      }
      // "blue" doubles as the default (matching the base body rule), so
      // pages without a color class reset correctly after leaving a
      // colored page.
      const color = winner >= 0 ? CSS_RULE_ORDER[winner] : "blue";
      if (document.body.dataset.bg !== color) {
        document.body.dataset.bg = color;
      }
    };

    sync();
    // Catches both page swaps (childList) and <Activity> hiding/showing
    // stashed pages (inline style mutations).
    const observer = new MutationObserver(sync);
    observer.observe(main, {
      attributeFilter: ["style", "class", "hidden"],
      attributes: true,
      childList: true,
      subtree: true,
    });
    return () => {
      observer.disconnect();
      delete document.body.dataset.bg;
    };
  }, []);

  return null;
}
